import React, { useRef, useState, useEffect, useCallback } from "react";

interface Props {
  onChange: (dataUrl: string | null) => void;
  width?: number;
  height?: number;
}

interface Point {
  x: number;
  y: number;
  pressure: number;
  time: number;
}

export default function SignatureCanvas({
  onChange,
  width: propWidth,
  height = 200,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(propWidth || 500);

  // Stroke history for smooth rendering
  const points = useRef<Point[]>([]);
  const allStrokes = useRef<Point[][]>([]);

  // ─── Responsive width ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (propWidth) {
      setCanvasWidth(propWidth);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      const rect = container.getBoundingClientRect();
      setCanvasWidth(Math.floor(rect.width - 2));
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, [propWidth]);

  // ─── Drawing helpers ──────────────────────────────────────────────────────────

  const drawBaseline = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.strokeStyle = "#e2e8f0";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(20, height - 30);
      ctx.lineTo(canvasWidth - 20, height - 30);
      ctx.stroke();
      ctx.setLineDash([]);
    },
    [canvasWidth, height]
  );

  /**
   * Calculate line width based on velocity between two points.
   * Faster movement = thinner stroke (like a real pen).
   */
  const getStrokeWidth = (p1: Point, p2: Point): number => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dt = Math.max(p2.time - p1.time, 1);
    const velocity = Math.sqrt(dx * dx + dy * dy) / dt;

    // Map velocity to width: slow = thick (3.5px), fast = thin (1.2px)
    const minWidth = 1.2;
    const maxWidth = 3.5;
    const width = maxWidth - Math.min(velocity * 0.4, maxWidth - minWidth);

    // Use pressure if available (pen/stylus)
    const pressure = p2.pressure > 0 ? p2.pressure : 0.5;
    return width * (0.6 + pressure * 0.8);
  };

  /**
   * Draw a smooth stroke segment using quadratic bezier curves
   * with variable width for a natural pen feel.
   */
  const drawStroke = useCallback(
    (ctx: CanvasRenderingContext2D, strokePoints: Point[]) => {
      if (strokePoints.length < 2) return;

      ctx.strokeStyle = "#000000";
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.fillStyle = "#000000";

      if (strokePoints.length === 2) {
        // Just two points: draw a simple line
        const w = getStrokeWidth(strokePoints[0], strokePoints[1]);
        ctx.lineWidth = w;
        ctx.beginPath();
        ctx.moveTo(strokePoints[0].x, strokePoints[0].y);
        ctx.lineTo(strokePoints[1].x, strokePoints[1].y);
        ctx.stroke();
        return;
      }

      // Draw with quadratic bezier curves for smoothness
      for (let i = 1; i < strokePoints.length; i++) {
        const prev = strokePoints[i - 1];
        const curr = strokePoints[i];
        const w = getStrokeWidth(prev, curr);

        ctx.lineWidth = w;
        ctx.beginPath();

        if (i === 1) {
          ctx.moveTo(prev.x, prev.y);
        } else {
          // Start from midpoint of previous segment for continuity
          const prevPrev = strokePoints[i - 2];
          const midX1 = (prevPrev.x + prev.x) / 2;
          const midY1 = (prevPrev.y + prev.y) / 2;
          ctx.moveTo(midX1, midY1);
        }

        // Draw to midpoint of current segment using control point
        if (i < strokePoints.length - 1) {
          const midX = (prev.x + curr.x) / 2;
          const midY = (prev.y + curr.y) / 2;
          ctx.quadraticCurveTo(prev.x, prev.y, midX, midY);
        } else {
          // Last segment: draw directly to end point
          ctx.quadraticCurveTo(prev.x, prev.y, curr.x, curr.y);
        }

        ctx.stroke();
      }
    },
    []
  );

  /**
   * Full redraw of all strokes (used after resize).
   */
  const redrawAll = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.clearRect(0, 0, canvasWidth, height);
      drawBaseline(ctx);

      for (const stroke of allStrokes.current) {
        drawStroke(ctx, stroke);
      }
    },
    [canvasWidth, height, drawBaseline, drawStroke]
  );

  // ─── Redraw when canvas resizes (with HiDPI support) ─────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    if (allStrokes.current.length > 0) {
      redrawAll(ctx);
    } else {
      ctx.clearRect(0, 0, canvasWidth, height);
      drawBaseline(ctx);
      setHasContent(false);
      onChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasWidth, height]);

  // ─── Point extraction ──────────────────────────────────────────────────────────

  const getPoint = useCallback(
    (
      e: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent
    ): Point => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      // Use CSS dimensions (not canvas.width which is scaled by dpr)
      const scaleX = canvasWidth / rect.width;
      const scaleY = height / rect.height;

      let clientX: number;
      let clientY: number;
      let pressure = 0.5;

      if ("touches" in e) {
        const touch = e.touches[0] || (e as TouchEvent).changedTouches?.[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
        if ("force" in touch) pressure = Math.max((touch as any).force, 0.1);
      } else {
        clientX = (e as MouseEvent).clientX;
        clientY = (e as MouseEvent).clientY;
      }

      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
        pressure,
        time: Date.now(),
      };
    },
    [canvasWidth, height]
  );

  // ─── Start drawing ────────────────────────────────────────────────────────────

  const startDrawing = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      const point = getPoint(e as any);
      points.current = [point];
      setIsDrawing(true);

      // Draw a small dot at the start
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.fillStyle = "#000000";
      ctx.beginPath();
      ctx.arc(point.x, point.y, 1.5, 0, Math.PI * 2);
      ctx.fill();
    },
    [getPoint]
  );

  // ─── Continue drawing ─────────────────────────────────────────────────────────

  const draw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing) return;
      e.preventDefault();

      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const point = getPoint(e as any);

      // Skip if too close to last point (prevents jitter)
      const last = points.current[points.current.length - 1];
      const dist = Math.sqrt(
        (point.x - last.x) ** 2 + (point.y - last.y) ** 2
      );
      if (dist < 1.5) return;

      points.current.push(point);
      setHasContent(true);

      // Draw the latest segment
      const len = points.current.length;
      if (len >= 2) {
        const segment =
          len >= 3
            ? points.current.slice(len - 3)
            : points.current.slice(len - 2);
        drawStroke(ctx, segment);
      }
    },
    [isDrawing, getPoint, drawStroke]
  );

  // ─── Stop drawing ─────────────────────────────────────────────────────────────

  const finishStroke = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (points.current.length > 0) {
      allStrokes.current.push([...points.current]);
      points.current = [];
    }

    const canvas = canvasRef.current;
    if (canvas && (hasContent || allStrokes.current.length > 0)) {
      onChange(canvas.toDataURL("image/png"));
    }
  }, [isDrawing, hasContent, onChange]);

  // ─── Global mouse/touch up listeners ──────────────────────────────────────────

  useEffect(() => {
    const handleUp = () => {
      if (isDrawing) {
        finishStroke();
      }
    };

    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDrawing, finishStroke]);

  // ─── Clear ────────────────────────────────────────────────────────────────────

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvasWidth, height);
    drawBaseline(ctx);

    allStrokes.current = [];
    points.current = [];
    setHasContent(false);
    onChange(null);
  }, [canvasWidth, height, drawBaseline, onChange]);

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className="w-full">
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={height}
          className="cursor-crosshair w-full block"
          style={{ touchAction: "none" }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={() => finishStroke()}
          onMouseLeave={() => finishStroke()}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={() => finishStroke()}
        />
        <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-t border-slate-200">
          <span className="text-xs text-slate-400">
            Draw your signature above
          </span>
          {hasContent && (
            <button
              onClick={clear}
              className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
